// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-UI-2 — funnels for any table that lists PRESCRIPTIONS through GET /rx.
//
// The Prescriptions worklist assembles its funnels inside StationQueue, welded to that page's
// tabs and column manager. Other tables read the same endpoint (the insurance queue) and had no
// way to reuse them, so they shipped with none. This is the assembly on its own: the server's
// column registry says what each column can do (rx/columns), offers its values (rx/facets), and
// the selections come back as the `filters` the list call already accepts.
//
// The page names the registry key for each header. Nothing here decides what is filterable:
// that is the registry's answer, per user, including the fields a role may not filter on.

import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as api from '../../api/client'
import {
  ColumnFilterButton, ColumnFilterChips, funnelToServerOps, type ColumnFilters,
} from './ColumnFilter'

export interface RxFunnelColumn {
  /** RxColumnRegistry key (rx_number, patient_name, payer_state, ...). */
  key: string
  label: string
}

export interface RxRegistryFunnels {
  filters: api.RxFilter[]
  sig: string
  funnel: (key: string) => ReactNode
  chips: ReactNode
  tableProps: { 'data-funnels': 'own' }
}

export function useRxRegistryFunnels(
  columns: RxFunnelColumn[],
  /** The scope the list itself is under, so an option that would return nothing is not offered. */
  scope: { states?: string; payerBlocked?: boolean; claimRejected?: boolean } = {},
): RxRegistryFunnels {
  const capsQ = useQuery({ queryKey: ['rx', 'columns'], queryFn: () => api.getRxColumnCaps(), staleTime: 600_000 })
  const caps = useMemo(() => new Map((capsQ.data?.items ?? []).map(c => [c.key, c])), [capsQ.data])

  const [colFilters, setColFilters] = useState<ColumnFilters>({})
  // Entity funnels bind ids; remember the names the facet returned so chips read as names.
  const [names, setNames] = useState<Record<string, Record<string, string>>>({})
  const remember = useCallback((key: string, pairs: { value: string; label: string }[]) => {
    setNames(m => {
      const forKey = { ...(m[key] ?? {}) }
      let changed = false
      for (const p of pairs) if (forKey[p.value] !== p.label) { forKey[p.value] = p.label; changed = true }
      return changed ? { ...m, [key]: forKey } : m
    })
  }, [])
  const labelFor = useCallback((key: string, value: string) => names[key]?.[value], [names])

  const filters = useMemo(() => {
    const out: api.RxFilter[] = []
    for (const [key, set] of Object.entries(colFilters)) {
      const cap = caps.get(key)
      if (!cap?.filterable || !set || set.size === 0) continue
      for (const p of funnelToServerOps(set, cap.ops).preds) out.push({ key, ...p })
    }
    return out
  }, [colFilters, caps])

  const funnel = (key: string): ReactNode => {
    const col = columns.find(c => c.key === key)
    if (!col) return null
    const cap = caps.get(key)
    if (!cap?.filterable) return (
      <span title={cap?.reason ?? `${col.label} cannot be filtered on this list.`}
        aria-label={`${col.label} filter unavailable`}
        style={{ display: 'inline-block', width: 14, opacity: 0.25, cursor: 'not-allowed', fontSize: 11 }}>⌀</span>
    )
    const isDate = cap.klass === 'date'
    const isNumeric = cap.klass === 'numeric'
    const isText = !cap.facetable && !isDate && !isNumeric && cap.ops.includes('contains')
    return (
      <ColumnFilterButton
        label={col.label}
        isDate={isDate}
        isNumeric={isNumeric}
        isText={isText}
        values={[]}
        fetchOptions={cap.facetable ? (term: string) => api.getRxFacet({
          column: key, q: term, ...scope,
          filters: filters.filter(f => f.key !== key),
        }) : undefined}
        labelFor={(v: string) => labelFor(key, v)}
        onLabels={pairs => remember(key, pairs)}
        selected={colFilters[key] ?? new Set()}
        onChange={next => setColFilters(f => ({ ...f, [key]: next }))} />
    )
  }

  const active = Object.values(colFilters).some(s => s && s.size > 0)
  const chips: ReactNode = active ? (
    <div data-funnel-chips>
      <ColumnFilterChips
        filters={colFilters}
        labels={Object.fromEntries(columns.map(c => [c.key, c.label]))}
        labelFor={labelFor}
        onClear={k => setColFilters(f => ({ ...f, [k]: new Set() }))}
        onClearAll={() => setColFilters({})} />
    </div>
  ) : null

  return {
    filters, sig: api.encodeRxFilters(filters).join('|'), funnel, chips,
    tableProps: { 'data-funnels': 'own' },
  }
}
